'use no memo';

import {
  createDisplayNameColumn,
  createEmailColumn,
  createFirstNameColumn,
  createLastLoginColumn,
  createLastNameColumn,
  TooltipGridActionsCellItem
} from '@treneo/features/admin';
import type { IOrganizationUserDto } from '@treneo/models';
import type { DateFormat, TimeFormat } from '@treneo/shared/constants';
import { CancelIcon, PersonAddIcon, RemoveCircleIcon } from '@treneo/shared/icons';
import { Chip } from '@treneo/shared/ui';

import PersonIcon from '@mui/icons-material/Person';
import type { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';

export type PendingInvitationByEmail = Map<string, { id: string }>;

export type CreateColumnsParams = {
  t: (key: string) => string;
  dateFormat?: DateFormat;
  timeFormat?: TimeFormat;
  onInviteUser: (user: IOrganizationUserDto) => void;
  onRemoveUser: (user: IOrganizationUserDto) => void;
  onRevokeTrainerRole?: (user: IOrganizationUserDto) => void;
  onCancelInvitation?: (invitationId: string) => void;
  pendingInvitationByEmail?: PendingInvitationByEmail;
  isInviting: boolean;
  isRemoving: boolean;
  isRevoking?: boolean;
  isCancelling?: boolean;
};

const TRAINER_LABEL_MAP: Record<
  'trainer' | 'invitation' | 'user',
  { labelKey: string; color: 'primary' | 'default' | 'warning' }
> = {
  trainer: { labelKey: 'trainers.trainer', color: 'primary' },
  invitation: { labelKey: 'trainers.invitationSentLabel', color: 'warning' },
  user: { labelKey: 'trainers.user', color: 'default' }
};

type IsTrainerCellProps = Readonly<{
  params: GridRenderCellParams<IOrganizationUserDto, boolean>;
  t: (key: string) => string;
  pendingInvitationByEmail?: PendingInvitationByEmail;
}>;

const IsTrainerCell = ({ params, t, pendingInvitationByEmail }: IsTrainerCellProps) => {
  const user = params.row;
  const isTrainer = params.value;
  const emailKey = user.email?.trim().toLowerCase() ?? '';
  const hasPendingInvitation = (pendingInvitationByEmail ?? null)?.has(emailKey) ?? false;
  let kind: 'trainer' | 'invitation' | 'user' = 'user';
  if (isTrainer) {
    kind = 'trainer';
  } else if (hasPendingInvitation) {
    kind = 'invitation';
  }
  const { labelKey, color } = TRAINER_LABEL_MAP[kind];
  const label = t(labelKey);
  return <Chip label={label} color={color} size='small' />;
};

type OrganizationUsersRowActionsCellProps = Readonly<
  CreateColumnsParams & { params: GridRowParams<IOrganizationUserDto> }
>;

const OrganizationUsersRowActionsCell = ({
  params,
  t,
  onInviteUser,
  onRemoveUser,
  onRevokeTrainerRole,
  onCancelInvitation,
  pendingInvitationByEmail,
  isInviting,
  isRemoving,
  isRevoking = false,
  isCancelling = false
}: OrganizationUsersRowActionsCellProps) => {
  const user = params.row;
  const actions: React.ReactNode[] = [];
  const emailKey = user.email?.trim().toLowerCase() ?? '';
  const pendingInvitation = pendingInvitationByEmail?.get(emailKey);

  if (pendingInvitation != null && onCancelInvitation != null) {
    actions.push(
      <TooltipGridActionsCellItem
        key='cancel-invitation'
        icon={<CancelIcon />}
        label={t('trainers.cancelInvitation')}
        onClick={() => onCancelInvitation(pendingInvitation.id)}
        disabled={isCancelling}
      />
    );
  } else if (user.isTrainer && onRevokeTrainerRole != null) {
    actions.push(
      <TooltipGridActionsCellItem
        key='revoke-trainer'
        icon={<PersonIcon />}
        label={t('trainers.revokeTrainerRole')}
        onClick={() => {
          void onRevokeTrainerRole(user);
        }}
        disabled={isRevoking}
      />
    );
  } else if (!user.isTrainer) {
    actions.push(
      <TooltipGridActionsCellItem
        key='invite'
        icon={<PersonAddIcon />}
        label={t('trainers.inviteTrainer')}
        onClick={() => {
          void onInviteUser(user);
        }}
        disabled={isInviting}
      />
    );
  }

  actions.push(
    <TooltipGridActionsCellItem
      key='remove'
      icon={<RemoveCircleIcon />}
      label={t('trainers.removeFromOrganization')}
      onClick={() => {
        void onRemoveUser(user);
      }}
      disabled={isRemoving}
    />
  );

  return <>{actions}</>;
};

const createIsTrainerColumn = (
  t: (key: string) => string,
  pendingInvitationByEmail?: PendingInvitationByEmail
): GridColDef<IOrganizationUserDto> => ({
  field: 'isTrainer',
  headerName: t('trainers.isTrainer'),
  width: 180,
  sortable: true,
  filterable: true,
  renderCell: params => (
    <IsTrainerCell
      params={params as GridRenderCellParams<IOrganizationUserDto, boolean>}
      t={t}
      pendingInvitationByEmail={pendingInvitationByEmail}
    />
  )
});

const createActionsColumn = (params: CreateColumnsParams): GridColDef<IOrganizationUserDto> => ({
  field: 'actions',
  type: 'actions',
  headerName: params.t('trainers.actions'),
  width: 300,
  getActions: getParams => [
    <OrganizationUsersRowActionsCell key='actions' {...params} params={getParams} />
  ]
});

export const createOrganizationUsersColumns = (
  params: CreateColumnsParams
): GridColDef<IOrganizationUserDto>[] => {
  const { t, dateFormat, timeFormat } = params;
  return [
    createEmailColumn<IOrganizationUserDto>(t, { headerName: t('trainers.email') }),
    createFirstNameColumn<IOrganizationUserDto>(t, { headerName: t('trainers.firstName') }),
    createLastNameColumn<IOrganizationUserDto>(t, { headerName: t('trainers.lastName') }),
    createDisplayNameColumn<IOrganizationUserDto>(t, { headerName: t('trainers.displayName') }),
    createLastLoginColumn<IOrganizationUserDto>(t, {
      headerName: t('trainers.lastLogin'),
      fallbackLabel: t('trainers.never'),
      dateFormat,
      timeFormat
    }),
    createIsTrainerColumn(t, params.pendingInvitationByEmail),
    createActionsColumn(params)
  ];
};

export const filterUsersByTrainerStatus = (
  users: IOrganizationUserDto[],
  showOnlyTrainers: boolean
): IOrganizationUserDto[] => (showOnlyTrainers ? users.filter(user => user.isTrainer) : users);
